import type { CodeOutcome } from "@hobbyshopify/backend-client";
import { Rx } from "subtree.js/core";
import type { CameraPhase } from "../../core/services/card-camera";
import type { Rect, Size } from "../../core/services/scan-image";
import type { Capture } from "../../core/services/scan-session";
import { FRONT_PROMPT } from "./scanner";

/** What the merchant says the next items are; `auto` lets the scanner decide. */
export type ScanKindChoice = "auto" | "card" | "sealed";

export class ScanCardState {
  readonly camera = new Rx<CameraPhase>("off");
  /** Why the live camera is not showing, as a sentence; null while it shows or starts. */
  readonly cameraProblem = new Rx<string | null>(null);
  /** Null when this browser gives the camera no torch. */
  readonly torch = new Rx<boolean | null>(null);
  readonly frontAndBack = new Rx(false);
  /** The session's kind: a run of boxes stays a run of boxes. */
  readonly kind = new Rx<ScanKindChoice>("auto");
  /** The preview of the front waiting for its back. */
  readonly heldFront = new Rx<string | null>(null);
  readonly prompt = new Rx(FRONT_PROMPT);
  /** This session's cards, newest first. */
  readonly captures = new Rx<readonly Capture[]>([]);
  readonly readyCount = new Rx(0);
  readonly unrecognisedCount = new Rx(0);
  readonly importing = new Rx(false);
  /** A shot or an import that could not be read. */
  readonly notice = new Rx<string | null>(null);
  /** Counts the notices said, so the same sentence said again is said anew. */
  readonly noticeSaid = new Rx(0);
  /** A barcode typed or sent by a barcode gun: it goes with the next picture, or is looked up alone. */
  readonly barcode = new Rx("");
  /** Why the barcode field holds no barcode; null when empty or valid. */
  readonly barcodeError = new Rx<string | null>(null);
  /** A valid barcode of an item that may be sealed: enough to look up with no photo. */
  readonly canLookUp = new Rx(false);
  /** Why the oldest barcode looked up alone named nothing, while it waits for its photo. */
  readonly codeOutcome = new Rx<CodeOutcome | null>(null);
}

export abstract class ScanCardActions {
  /** The preview mounted (the camera starts) or gone (it stops). */
  abstract cameraShown(video: HTMLVideoElement | null): void;
  /** The guide's crop of the frame showing; `view` is the preview's size, `guide` the frame drawn in it. */
  abstract shoot(view: Size, guide: Rect): Promise<void>;
  /** Imports and photos from the system camera, in the order picked. */
  abstract importPictures(files: readonly File[]): Promise<void>;
  abstract setFrontAndBack(on: boolean): void;
  abstract setKind(kind: ScanKindChoice): void;
  abstract setBarcode(value: string): void;
  /** Sends the barcode alone: a sealed box it names lands on Review with no photo. */
  abstract lookUpBarcode(): void;
  /** The barcode waiting for its photo, sent again on its attempt: for a lookup that was down. */
  abstract retryBarcode(): void;
  abstract discardHeldFront(): void;
  abstract toggleTorch(): Promise<void>;
  abstract retry(id: string): void;
  abstract discard(id: string): void;
  /** Done and ✕ alike, as iOS. */
  abstract done(): void;
}
